import { useEffect, useRef, useState } from "react";
import { send } from "../api.js";
import { formatDate } from "../format.js";
import { Dialog } from "../ui/Dialog.js";
import type { LessonBlockDetail } from "./staff.js";
import { formatMoney } from "./money.js";

export interface LegacyCreditReleasePreview {
  lesson_block_id: string;
  expected_revision: string;
  can_release: boolean;
  refusal_reason: string | null;
  existing_balance: number;
  projected_balance: number;
  transactions_to_record: number;
  coupon_id: number;
  coupon_limit: number;
  coupon_used: number;
  coupon_remaining: number;
  purchase_payment_id: number;
  payment_amount: string;
  expires_at: string;
}

export function LegacyCreditRelease({ detail, studentName, onCancel, onApply }: {
  detail: LessonBlockDetail;
  studentName: string;
  onCancel: () => void;
  onApply: (mutation: () => Promise<LessonBlockDetail>, description: string) => Promise<void>;
}) {
  const couponInput = useRef<HTMLInputElement>(null);
  const previewArea = useRef<HTMLDivElement>(null);
  const [couponId, setCouponId] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const [appointments, setAppointments] = useState("");
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");
  const [ownershipVerified, setOwnershipVerified] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [preview, setPreview] = useState<LegacyCreditReleasePreview | null>(null);
  const [phase, setPhase] = useState<"checking" | "releasing" | null>(null);
  const busy = phase !== null;
  const [problem, setProblem] = useState<string | null>(null);
  const path = `/staff-api/api/v1/lesson-blocks/${encodeURIComponent(detail.block.id)}/legacy-release`;
  const ids = appointments.trim() ? appointments.trim().split(/[\s,]+/).map(Number) : [];
  const valid = Number.isSafeInteger(Number(couponId)) && Number(couponId) > 0 && couponCode.trim().length > 0 && reference.trim().length >= 3 && ownershipVerified && ids.length <= 100 && ids.every((id) => Number.isSafeInteger(id) && id > 0) && new Set(ids).size === ids.length;
  const evidence = { coupon_id: Number(couponId), coupon_code: couponCode.trim(), evidenced_appointment_ids: ids, issuance_reference: reference.trim(), reason: reason.trim(), ownership_verified: ownershipVerified };

  useEffect(() => {
    if (preview) previewArea.current?.scrollIntoView?.({ block: "nearest" });
  }, [preview]);

  function edit(change: () => void) {
    change();
    setPreview(null);
    setConfirmed(false);
    setProblem(null);
  }

  async function check() {
    if (!valid || busy) return;
    setPhase("checking");
    setProblem(null);
    setPreview(null);
    setConfirmed(false);
    try {
      setPreview(await send<LegacyCreditReleasePreview>("POST", `${path}/preview`, evidence));
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : "Could not verify the original purchase.");
    } finally { setPhase(null); }
  }

  async function release() {
    if (!preview?.can_release || !valid || !confirmed || busy) return;
    setPhase("releasing");
    setProblem(null);
    try {
      await onApply(async () => {
        const result = await send<LessonBlockDetail>("POST", path, { ...evidence, expected_revision: preview.expected_revision });
        if (result.block.id !== detail.block.id || result.block.status !== "active" || result.block.redemption_authority !== "amelia_coupon") throw new Error("Release was not confirmed. Reload this block before trying again.");
        return result;
      }, `Legacy credits released for ${studentName}`);
    } catch (cause) {
      setProblem(cause instanceof Error ? cause.message : "Release could not be confirmed. Reload the block before retrying.");
    } finally { setPhase(null); }
  }

  return <Dialog title="Review legacy credits" description={`${studentName} · ${detail.product.display_name}`} onClose={onCancel} busy={busy} size="medium" initialFocusRef={couponInput}>
    <p>This older paid block needs its original coupon linked and any lessons already used accounted for. Check the original issuance record before continuing.</p>
    <fieldset className="form-stack" disabled={busy} style={{ border: 0, padding: 0, margin: 0 }}>
      <label className="field-stack">Original coupon number<input ref={couponInput} type="number" min={1} value={couponId} onChange={(event) => edit(() => setCouponId(event.target.value))} /></label>
      <label className="field-stack">Original coupon code<input maxLength={32} value={couponCode} onChange={(event) => edit(() => setCouponCode(event.target.value))} /></label>
      <label className="field-stack">Where you verified this coupon<input maxLength={500} placeholder="Original issuance register and row, or receipt reference" value={reference} onChange={(event) => edit(() => setReference(event.target.value))} /></label>
      <label className="field-stack">Lessons already booked with this coupon<input value={appointments} onChange={(event) => edit(() => setAppointments(event.target.value))} placeholder="Appointment numbers separated by commas; leave blank if unused" /></label>
      <p className="small muted">Include every lesson that used this coupon, including future bookings. Each appointment will be checked against this student, tutor and coupon.</p>
      <label className="field-stack">Note (optional)<textarea maxLength={1000} value={reason} onChange={(event) => edit(() => setReason(event.target.value))} /></label>
      <label className="row"><input type="checkbox" checked={ownershipVerified} onChange={(event) => edit(() => setOwnershipVerified(event.target.checked))} /><span style={{ flex: 1 }}>I verified that this original coupon was issued for this student's exact purchase.</span></label>
    </fieldset>
    {problem && <p role="alert" className="error">{problem}</p>}
    <button type="button" className="action-secondary" disabled={!valid || busy} onClick={() => void check()}>{phase === "checking" ? "Checking…" : "Preview release"}</button>
    {preview && <div ref={previewArea} className="notice" aria-live="polite" style={{ marginTop: 16 }}>
      <p><strong>Original payment verified:</strong> {formatMoney(preview.payment_amount, detail.block.payment_currency)} · payment {preview.purchase_payment_id}</p>
      <p>Recorded balance: {preview.existing_balance}. Balance after checked lesson history: {preview.projected_balance}. Original coupon remaining: {preview.coupon_remaining}.</p>
      <p>Expiry: {formatDate(preview.expires_at)}.</p>
      {preview.can_release ? <>
        <p><strong>Ready to release:</strong> link the existing coupon and activate {preview.projected_balance} remaining credits. {preview.transactions_to_record > 0 ? `${preview.transactions_to_record} historical credit uses will be recorded.` : "The existing credit history already matches."}</p>
        <label className="row"><input type="checkbox" disabled={busy} checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} /><span style={{ flex: 1 }}>Release this reviewed block using its original coupon.</span></label>
        <button type="button" className="action-primary" disabled={!confirmed || busy} onClick={() => void release()}>{phase === "releasing" ? "Releasing…" : "Release reviewed credits"}</button>
      </> : <p role="alert">{preview.refusal_reason ?? "The balance and coupon usage do not match. Check the missing lesson history before releasing."}</p>}
    </div>}
  </Dialog>;
}
